"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Code2,
  ListTree,
  PanelRight,
  Play,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { useAuth } from "@/components/providers/auth-provider";
import { fetcher } from "@/lib/api";
import { formatNumber } from "@/lib/format-datetime";
import { readSessionHint } from "@/lib/session-hint";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import type { LearnAccessLevel, LearnPart, LearnTrackDetail, LearnViewer } from "@/features/learn/api";
import {
  decodeRunCode,
  LESSON_LANG_STORAGE_KEY,
  programStdinIn,
  type LessonProgramMeta,
} from "@/features/lessons/components/LessonRichContent";
import { getLanguageBySlug } from "@/features/playground/config/languages";
import { RUN_LINK_VERSION_PARAM } from "@/features/playground/lib/run-link-version";
import type { JudgeRuntimeCatalogue } from "@/features/judge/types";
import {
  completeLearnLesson,
  fetchLearnLessonForViewer,
  fetchLearnProblemSubmission,
  learnLessonHref,
  learnLessonViewerKey,
  postLearnEvent,
  submitLearnProblem,
  type LearnJudgeVerdict,
  type LearnReaderPayload,
} from "./api";
import { fetchJudgeRuntimes } from "@/features/judge/api";
import type { LearnJudgeBar, LearnJudgeStatus, LearnRunRequest } from "./LearnEditorPane";
import { lessonAccessPending, lessonRefFromHref } from "./access-pending";
import { LearnCheckpoint } from "./LearnCheckpoint";
import { LearnLessonBody } from "./LearnLessonBody";
import { LearnModuleTest } from "./LearnModuleTest";
import { LearnProblemLock } from "./LearnProblemLock";
import { PROBLEM_LOCK_SELECTOR } from "@/features/lessons/lib/hint-ladder";
import { lessonKind, LearnReaderRail, type LearnSection } from "./LearnReaderRail";
import { LearnSyllabusPanel } from "./LearnSyllabusPanel";
import { sectionHeadings, sectionLabel } from "./section-headings";
import {
  canRunBlockInTrack,
  GRADED_VERSION_BY_LANGUAGE,
  playgroundLanguageForTrack,
} from "./track-language";
import { signInHref as buildSignInHref } from "@/lib/post-login-redirect";

/**
 * `LN-UI01`: the editor arrives after the lesson, never before it. Monaco is a
 * client-only chunk, mounted one idle tick after hydration at the earliest, so
 * the top bar's links answer a click while the page is still settling.
 *
 * `LN-UI05`: and it is the Playground's embed composition, not the full IDE
 * (`LearnEditorPane` says why).
 */
const LearnEditorPane = dynamic(() => import("./LearnEditorPane").then((m) => m.LearnEditorPane), {
  ssr: false,
});

/**
 * The Learn reader (`LN-RDR01`, docs/35 §5), redrawn by `LN-UI05` from the
 * canvas page "Lesson reader" the founder accepted on 2026-09-23.
 *
 * **The layout.** A top bar that says where the reader is (track, module,
 * "Lesson 3 of 8") with the language switch, the syllabus and the editor; a
 * reading-progress hairline under it; the module's rail on the left
 * (`LearnReaderRail`); the lesson in a `read` column; the editor on the right
 * only when asked for. Below 1024 px (`lg`) the rail goes, the syllabus is a dialog
 * and the editor is a full-screen sheet behind a bottom bar.
 *
 * **The editor opens on Run** (founder decision 1ক). Closed by default, so the
 * reading column is wide; a code block's Run button opens it with that block's
 * program and runs it, an authored "Run in Compiler" link opens it with the
 * starter to edit, and `r` or the top bar's Editor button opens it on the
 * lesson's first program. Once open it stays open on the next lesson, because
 * the choice is kept in this browser. A locked lesson and a module test have no
 * editor at all: there is nothing on those pages to run.
 *
 * **One design for every track** (founder, 2026-09-23). Nothing here names C:
 * the editor runs each program in the language the lesson gives it (the block's
 * class or the Run link's path, `RV-12`) and falls back to
 * `playgroundLanguageForTrack`, which code blocks can run comes from
 * `canRunBlockInTrack`, and every label from the track and lesson payloads, so
 * a new track gets this reader once its slug is in that map.
 *
 * **The two reads.** The page is server-rendered and ISR-cached from the
 * anonymous payload, which carries a `free` lesson in full and a `core` or `pro`
 * lesson as its teaser; a signed-in reader's own payload replaces it from the
 * browser. Caching the signed-in half would serve one learner's paid lesson to
 * the next visitor (`web-ssr-was-a-skeleton`).
 *
 * **Nothing here decides access.** `lesson.access` arrives decided by the
 * server, and this component draws one of three things from it.
 */

const PANE_WIDTH_KEY = "progsity:learn-pane-width";
const PANE_OPEN_KEY = "progsity:learn-pane-open";
/** Below this the rail goes and the editor is a sheet, not a column (docs/35 §5 item 1). */
const TWO_PANE_MIN_PX = 1024;
const MIN_PANE_PERCENT = 30;
/**
 * `PG-UX09`: a floor under the percentage. 30% of a 1024 px reader is about
 * 286 px, narrower than the editor pane's header can be (wordmark, reset, Run
 * and Close need about 330 px) and too narrow to read a line of code in.
 */
const MIN_PANE_PX = 360;
const MAX_PANE_PERCENT = 65;
const DEFAULT_PANE_PERCENT = 48;
/** One arrow key on the splitter (`RV-14`). */
const SPLITTER_KEY_STEP = 3;
/** The top bar (56 px) and the progress hairline (3 px): where sticky columns start. */
const TOP_PX = 59;

const ACCESS_TONE: Record<LearnAccessLevel, StatusTone> = {
  free: "success",
  core: "info",
  pro: "brand",
};
const ACCESS_LABEL = { free: "accessFree", core: "accessCore", pro: "accessPro" } as const;
const KIND_LABEL = {
  read: "lessonKindReading",
  problems: "lessonKindProblems",
  test: "lessonKindTest",
} as const;
const KIND_ICON = { read: BookOpen, problems: Code2, test: ClipboardCheck } as const;

function readStoredPercent(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw === null ? Number.NaN : Number(raw);
    if (!Number.isFinite(value)) return fallback;
    return Math.min(MAX_PANE_PERCENT, Math.max(MIN_PANE_PERCENT, value));
  } catch {
    return fallback;
  }
}

async function fetchTrackViewer(trackSlug: string): Promise<LearnViewer | null> {
  const res = await fetcher<{ track: LearnTrackDetail; viewer: LearnViewer | null }>(
    `/v1/learn/tracks/${encodeURIComponent(trackSlug)}`,
  );
  return res.success ? (res.data.viewer ?? null) : null;
}

/** The lesson's first "Run in Compiler" link, as stored. */
function firstRunHref(html: string | null): string | null {
  const match = html?.match(/<a[^>]*class="pc-run"[^>]*href="([^"]+)"/);
  return match?.[1] ? match[1].replace(/&amp;/g, "&") : null;
}

/**
 * `RV-10`: the language of the lesson's first Run link, from its path alone, so
 * the server and the first client render agree. It is the pane's fallback on a
 * track the map does not name.
 */
function firstProgramLanguage(html: string | null): string | null {
  const slug = firstRunHref(html)?.match(/\/playground\/([^/?#]+)/)?.[1];
  return slug ? (getLanguageBySlug(slug)?.id ?? null) : null;
}

/**
 * The lesson's first "Run in Compiler" program: what `r` opens the editor on,
 * with the version its link names (`LN-WEB02`), the language its path names
 * (`RV-12`) and the input its block carries (`RV-13`). Client only.
 */
function firstLessonProgram(
  html: string | null,
): { code: string; languageVersion?: string; languageId?: string; stdin?: string } | null {
  const href = firstRunHref(html);
  if (!href) return null;
  const code = decodeRunCode(href);
  if (!code) return null;
  const languageVersion = new URL(href, window.location.origin).searchParams.get(
    RUN_LINK_VERSION_PARAM,
  );
  const languageId = firstProgramLanguage(html);
  const stdin = html ? programStdinIn(new DOMParser().parseFromString(html, "text/html"), code) : undefined;
  return {
    code,
    ...(languageVersion ? { languageVersion } : {}),
    ...(languageId ? { languageId } : {}),
    ...(stdin ? { stdin } : {}),
  };
}

function sameSections(a: LearnSection[], b: LearnSection[]): boolean {
  return a.length === b.length && a.every((row, i) => row.id === b[i]?.id && row.label === b[i]?.label);
}

/**
 * `LN-PRB01`: what the reader knows about one judged problem. The server's
 * state (`viewer.problems`) is the resting truth; `status` is this tab's own
 * submit in flight, and the two are merged so a reload never loses a verdict
 * and a verdict never overwrites a submit that is still being judged.
 */
type JudgeEntry = {
  accepted: boolean;
  attempts: number;
  latestVerdict: LearnJudgeVerdict | null;
  status: LearnJudgeStatus;
};

const IDLE: LearnJudgeStatus = { kind: "idle" };
/** How long one verdict is waited for before the bar gives up and says so. */
const JUDGE_POLL_MS = 2_000;
const JUDGE_WAIT_MS = 120_000;
/** The settlement tick runs every 10 s; the completion is re-read after it. */
const SETTLEMENT_REFRESH_MS = 12_000;

/**
 * Paints each problem card's badge from the judge state (`LN-PRB01`). Raw DOM
 * for the same reason the hint ladder is: the card is stored HTML and cannot
 * carry a component. Idempotent: one badge per card, updated in place.
 */
function paintJudgeBadges(
  root: HTMLElement,
  judge: Record<string, JudgeEntry>,
  labels: { accepted: string; tried: string },
): void {
  root.querySelectorAll<HTMLElement>(".pc-exercise").forEach((card) => {
    const key = card.querySelector(".pc-exercise-num")?.textContent?.match(/problem\s*\d+\s*:\s*([a-z0-9-]+)/i)?.[1];
    const entry = key ? judge[key] : undefined;
    let badge = card.querySelector<HTMLElement>(".pc-judge-badge");
    if (!entry || (!entry.accepted && entry.attempts === 0)) {
      badge?.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "pc-judge-badge";
      (card.querySelector(".pc-exercise-head") ?? card).append(badge);
    }
    badge.dataset.judge = entry.accepted ? "accepted" : "tried";
    badge.textContent = entry.accepted ? labels.accepted : labels.tried;
  });
}

/** How long the reader's own payload counts as fresh, for its read and for a prefetch alike. */
const LESSON_VIEWER_STALE_MS = 30_000;

/** The session hint does not change under a page that is open, so nothing to subscribe to. */
function subscribeNothing(): () => void {
  return () => {};
}

function readSessionHintSnapshot() {
  return readSessionHint();
}

function unknownHint() {
  return null;
}

export function LearnReaderClient({
  initial,
  parts,
  locale,
}: {
  initial: LearnReaderPayload;
  /** The syllabus, from the track route the page already reads. */
  parts: LearnPart[];
  locale: Locale;
}) {
  const t = useTranslations("learn.reader");
  const tl = useTranslations("learn");
  const router = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  // Read during render through the store hook: the server and the hydrating
  // render see "unknown", a client navigation sees the browser's own hint.
  const sessionHint = useSyncExternalStore(subscribeNothing, readSessionHintSnapshot, unknownHint);

  const { trackSlug } = initial.track;
  const { moduleKey, lessonKey } = initial.lesson;

  /**
   * The viewer's own payload. `initial` is the placeholder rather than a
   * separate loading state, so a signed-in reader sees the open half of the page
   * immediately and the gated half when it lands.
   */
  const { data, isPlaceholderData, isError } = useQuery({
    queryKey: learnLessonViewerKey(trackSlug, moduleKey, lessonKey),
    queryFn: () => fetchLearnLessonForViewer(trackSlug, moduleKey, lessonKey),
    enabled: isAuthenticated,
    staleTime: LESSON_VIEWER_STALE_MS,
    placeholderData: initial,
  });
  const payload = data ?? initial;
  const { lesson, nav, viewer } = payload;
  /**
   * `LN-RDR03`: a locked lesson whose real answer has not landed yet draws a
   * neutral frame, not the sign-in card a signed-in learner used to see blink.
   */
  const accessPending = lessonAccessPending({
    access: lesson.access,
    isPlaceholderData: Boolean(isPlaceholderData),
    isError,
    isAuthenticated,
    authLoading,
    sessionHint,
  });

  /**
   * `LN-RDR03`: a lesson link the pointer reaches (or the keyboard focuses)
   * starts that lesson's own read, so the click usually finds it in the cache
   * and the lesson opens without a wait. One handler on the reader covers the
   * rail, the syllabus, previous and next; signed-out readers have nothing to
   * prefetch, because the page itself already carries their payload.
   */
  const prefetchLesson = useCallback(
    (ref: { moduleKey: string; lessonKey: string } | null) => {
      if (!isAuthenticated || !ref) return;
      if (ref.moduleKey === moduleKey && ref.lessonKey === lessonKey) return;
      void queryClient.prefetchQuery({
        queryKey: learnLessonViewerKey(trackSlug, ref.moduleKey, ref.lessonKey),
        queryFn: () => fetchLearnLessonForViewer(trackSlug, ref.moduleKey, ref.lessonKey),
        staleTime: LESSON_VIEWER_STALE_MS,
      });
    },
    [isAuthenticated, lessonKey, moduleKey, queryClient, trackSlug],
  );
  const prefetchLessonLink = useCallback(
    (event: { target: EventTarget | null }) => {
      if (!(event.target instanceof Element)) return;
      const href = event.target.closest("a[href]")?.getAttribute("href");
      if (href) prefetchLesson(lessonRefFromHref(href, trackSlug));
    },
    [prefetchLesson, trackSlug],
  );
  // Done, J and K go to the neighbours by `router.push`, with no link to point
  // at, so a signed-in reader's previous and next lessons are read up front.
  const nextRef = payload.nav.next;
  const prevRef = payload.nav.prev;
  useEffect(() => {
    prefetchLesson(nextRef);
    prefetchLesson(prevRef);
  }, [nextRef, prevRef, prefetchLesson]);

  // The same query key the track page's syllabus uses, so the two never disagree.
  const { data: trackViewer } = useQuery({
    queryKey: ["learn", "track", trackSlug, "viewer"],
    queryFn: () => fetchTrackViewer(trackSlug),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  /**
   * `LN-UI05`: the language is the reader's, from the first render. `/bn`
   * starts in Bangla on the server, so the first paint is Bangla; a choice the
   * reader made before (the same key the course player keeps) wins after that.
   */
  const [contentLang, setContentLang] = useState<"en" | "bn">(locale === "bn" ? "bn" : "en");
  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [paneOpen, setPaneOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(false);
  const [panePercent, setPanePercent] = useState(DEFAULT_PANE_PERCENT);
  const [twoPane, setTwoPane] = useState(true);
  const [runRequest, setRunRequest] = useState<LearnRunRequest | null>(null);
  /** `LN-UI01`: the editor mounts one idle tick after hydration, never during it. */
  const [editorReady, setEditorReady] = useState(false);
  const [sections, setSections] = useState<LearnSection[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  /** `LN-RDR02`: "Done, next lesson". */
  const [marking, setMarking] = useState(false);
  const [markedDone, setMarkedDone] = useState(false);
  const [markError, setMarkError] = useState(false);
  /** `LN-PRB01`: the judged problems, and the one the editor is open on. */
  const [judge, setJudge] = useState<Record<string, JudgeEntry>>({});
  const [activeProblemKey, setActiveProblemKey] = useState<string | null>(null);
  /** `LN-ACC01`: the nodes the hint ladder left in locked Pro problem cards. */
  const [lockMounts, setLockMounts] = useState<HTMLElement[]>([]);
  const bodyRootRef = useRef<HTMLElement | null>(null);
  const judgeRef = useRef(judge);
  judgeRef.current = judge;
  const frameRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  /**
   * The track's language (`RV-10`), and the pane's fallback: a program the
   * lesson hands the pane carries its own (`RV-12`). On a track the map does
   * not name, the lesson's first Run link decides, and a lesson with neither
   * has no editor rather than a C one.
   */
  const trackLanguageId = playgroundLanguageForTrack(trackSlug);
  const languageId = trackLanguageId ?? firstProgramLanguage(lesson.content_en);
  /** Nothing to run on a locked lesson or a test, so no editor there. */
  const editorAllowed = lesson.access === "granted" && lesson.type !== "quiz" && languageId !== null;

  // Remembered per browser. Read in an effect, never during render: the server
  // has no localStorage, and a value read in render is a hydration mismatch.
  useEffect(() => {
    setPanePercent(readStoredPercent(PANE_WIDTH_KEY, DEFAULT_PANE_PERCENT));
    try {
      setPaneOpen(window.localStorage.getItem(PANE_OPEN_KEY) === "true");
      const stored = window.localStorage.getItem(LESSON_LANG_STORAGE_KEY);
      if (stored === "en" || stored === "bn") setContentLang(stored);
    } catch {
      // Private mode: the defaults stand.
    }
  }, []);

  useEffect(() => {
    const idle = window.requestIdleCallback;
    if (typeof idle === "function") {
      const handle = idle(() => setEditorReady(true), { timeout: 2000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    const timer = window.setTimeout(() => setEditorReady(true), 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${TWO_PANE_MIN_PX}px)`);
    const apply = () => setTwoPane(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  const persist = useCallback((key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Private mode: the preference just does not survive the tab.
    }
  }, []);

  const selectLang = useCallback(
    (next: "en" | "bn") => {
      setContentLang(next);
      persist(LESSON_LANG_STORAGE_KEY, next);
    },
    [persist],
  );

  /** Run, a "Run in Compiler" link, `r` and the Editor button all come here. */
  const openEditor = useCallback(
    (sourceCode?: string, autoRun = false, meta: LessonProgramMeta = {}) => {
      if (!editorAllowed) return;
      // `EX-FNC09`: a function problem's card opens the pane on the judge's
      // starter in the problem's own language, never runs it (a function alone
      // is not a program), and never carries the card's input.
      const functionProblem = meta.problemKey
        ? lesson.problems.find((problem) => problem.key === meta.problemKey && problem.function)
        : undefined;
      if (functionProblem?.function) {
        setRunRequest({
          nonce: Date.now(),
          sourceCode: functionProblem.function.starter,
          autoRun: false,
          languageVersion: meta.languageVersion,
          languageId: functionProblem.languageKey,
        });
      } else if (sourceCode) {
        setRunRequest({
          nonce: Date.now(),
          sourceCode,
          autoRun,
          languageVersion: meta.languageVersion,
          languageId: meta.languageId,
          stdin: meta.stdin,
        });
      }
      // `LN-PRB01`: code from inside a problem card binds the pane's judge bar
      // to that problem; code from anywhere else leaves it where it was.
      if (meta.problemKey) setActiveProblemKey(meta.problemKey);
      setEditorReady(true);
      if (twoPane) {
        setPaneOpen(true);
        persist(PANE_OPEN_KEY, "true");
      } else {
        setSheetOpen(true);
      }
    },
    [editorAllowed, lesson.problems, persist, twoPane],
  );

  const closeEditor = useCallback(() => {
    setSheetOpen(false);
    setPaneOpen(false);
    persist(PANE_OPEN_KEY, "false");
  }, [persist]);

  const goTo = useCallback(
    (ref: { moduleKey: string; lessonKey: string } | null) => {
      if (ref) router.push(learnLessonHref(locale, trackSlug, ref));
    },
    [locale, router, trackSlug],
  );

  // `j` / `k` for lessons and `r` for run (docs/35 §5 item 6), plus the arrow
  // keys the course player already answers. Everything bails inside an editor or
  // a field: Monaco owns its own keys, and so does a checkpoint's button.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (target.isContentEditable) return;
      if (typeof target.closest === "function" && target.closest("[data-learn-pane]")) return;

      if (event.key === "j" || event.key === "ArrowRight") goTo(nav.next);
      else if (event.key === "k" || event.key === "ArrowLeft") goTo(nav.prev);
      else if (event.key === "r") openEditor();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [goTo, nav.next, nav.prev, openEditor]);

  // The splitter between the lesson and the editor.
  const onSplitterDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const frame = frameRef.current;
      if (!frame) return;
      event.preventDefault();
      const move = (moveEvent: PointerEvent) => {
        const rect = frame.getBoundingClientRect();
        const fromRight = ((rect.right - moveEvent.clientX) / rect.width) * 100;
        setPanePercent(Math.min(MAX_PANE_PERCENT, Math.max(MIN_PANE_PERCENT, fromRight)));
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        setPanePercent((value) => {
          persist(PANE_WIDTH_KEY, String(Math.round(value)));
          return value;
        });
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [persist],
  );

  const pick = useCallback(
    (row: { title_en: string; title_bn: string | null }) =>
      contentLang === "bn" && row.title_bn ? row.title_bn : row.title_en,
    [contentLang],
  );
  const num = (n: number) => formatNumber(n, contentLang);
  const title = pick(lesson);
  const trackTitle =
    locale === "bn" && payload.track.title_bn ? payload.track.title_bn : payload.track.title_en;
  const moduleTitle = pick(lesson.module);

  const allModules = useMemo(() => parts.flatMap((part) => part.modules), [parts]);
  const moduleIndex = allModules.findIndex((m) => m.id === lesson.module.id);
  const currentModule = moduleIndex >= 0 ? allModules[moduleIndex] : undefined;
  const moduleLessons = currentModule?.lessons ?? [];
  const positionInModule = moduleLessons.findIndex((row) => row.id === lesson.id) + 1;
  const nextModules = useMemo(
    () =>
      moduleIndex >= 0
        ? allModules
            .slice(moduleIndex + 1, moduleIndex + 4)
            .map((module, i) => ({ number: moduleIndex + 1 + i, module }))
        : [],
    [allModules, moduleIndex],
  );
  const completedLessonIds = useMemo(() => {
    const ids = new Set(trackViewer?.completedLessonIds ?? []);
    if (markedDone || viewer?.completed) ids.add(lesson.id);
    return [...ids];
  }, [lesson.id, markedDone, trackViewer?.completedLessonIds, viewer?.completed]);
  const currentRow = moduleLessons.find((row) => row.id === lesson.id);
  const kind = currentRow ? lessonKind(currentRow) : lesson.type === "quiz" ? "test" : "read";
  const KindIcon = KIND_ICON[kind];
  const positionText =
    positionInModule > 0
      ? t("positionOf", { n: num(positionInModule), total: num(moduleLessons.length) })
      : null;
  const moduleLabel =
    moduleIndex >= 0 ? `${t("railModule", { n: num(moduleIndex) })} · ${moduleTitle}` : moduleTitle;

  const solutions = useMemo(
    () =>
      Object.fromEntries(
        lesson.problems.map((problem) => [
          problem.key,
          { languageKey: problem.languageKey, solutionCode: problem.solutionCode },
        ]),
      ),
    [lesson.problems],
  );

  const recordStep = useCallback(
    (kindOfStep: "hint" | "checkpoint", step: string) => {
      if (!isAuthenticated) return;
      // Fire and forget: the server is idempotent per learner, lesson and step.
      void postLearnEvent({ trackSlug, moduleKey, lessonKey, kind: kindOfStep, step }).catch(() => {});
    },
    [isAuthenticated, lessonKey, moduleKey, trackSlug],
  );

  /**
   * `LN-RDR02`, founder decision of 2026-09-23: a lesson is finished when the
   * reader says so, never by scrolling to the end.
   */
  const markDone = useCallback(async () => {
    setMarking(true);
    setMarkError(false);
    try {
      await completeLearnLesson({ trackSlug, moduleKey, lessonKey });
      setMarkedDone(true);
      void queryClient.invalidateQueries({ queryKey: ["learn", "track", trackSlug, "viewer"] });
      void queryClient.invalidateQueries({ queryKey: ["learn", "tracks", "viewer"] });
      void queryClient.invalidateQueries({
        queryKey: ["learn", "lesson", trackSlug, moduleKey, lessonKey, "viewer"],
      });
      if (nav.next) router.push(learnLessonHref(locale, trackSlug, nav.next));
    } catch {
      setMarkError(true);
    } finally {
      setMarking(false);
    }
  }, [lessonKey, locale, moduleKey, nav.next, queryClient, router, trackSlug]);

  const isDone = markedDone || viewer?.completed === true;
  const signInHref = buildSignInHref(locale, pathname);
  /** A readable text lesson ends on the Done card, which carries the next link itself. */
  const showFinish = lesson.type !== "quiz" && lesson.access === "granted";

  /* ── `LN-PRB01`: the judged problems ─────────────────────────────────────── */

  const judgedKeys = useMemo(
    () => lesson.problems.filter((problem) => problem.judged).map((problem) => problem.key),
    [lesson.problems],
  );
  /** `LN-ACC01`: the Pro problems this reader cannot reach (the server decides). */
  const lockedKeys = useMemo(
    () => lesson.problems.filter((problem) => problem.locked).map((problem) => problem.key),
    [lesson.problems],
  );
  /**
   * The judged problems this reader can submit to, which is what Done counts
   * (docs/35 §5 item 3): all of them with Pro, the free ones without.
   */
  const reachableKeys = useMemo(
    () => judgedKeys.filter((key) => !lockedKeys.includes(key)),
    [judgedKeys, lockedKeys],
  );
  const lockedJudgedCount = judgedKeys.length - reachableKeys.length;
  /**
   * `LN-RDR03`'s rule for the per-problem locks: the anonymous placeholder says
   * every Pro problem is locked, so while a signed-in reader's own answer is on
   * its way the Pro cards and the Done card's Pro line wait rather than blink.
   */
  const problemLocksPending =
    lockedKeys.length > 0 &&
    Boolean(isPlaceholderData) &&
    !isError &&
    (isAuthenticated || (authLoading && sessionHint !== "none"));
  const acceptedCount = reachableKeys.filter((key) => judge[key]?.accepted).length;
  /** A problems lesson is finished by the judge, never by the Done button alone. */
  const finishGated = reachableKeys.length > 0 && acceptedCount < reachableKeys.length && !isDone;

  // The server's resting state, merged under any submit this tab has in flight.
  useEffect(() => {
    const rows = viewer?.problems ?? [];
    setJudge((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        const current = prev[row.key];
        const busy = current?.status.kind === "submitting" || current?.status.kind === "judging";
        next[row.key] = {
          accepted: row.accepted || Boolean(current?.accepted),
          attempts: Math.max(row.attempts, current?.attempts ?? 0),
          latestVerdict: (row.latest?.verdict as LearnJudgeVerdict | null) ?? current?.latestVerdict ?? null,
          status: busy ? current.status : (current?.status ?? IDLE),
        };
      }
      return next;
    });
  }, [viewer?.problems]);

  const judgeLabels = useMemo(
    () => ({ accepted: t("judgeVerdict.accepted"), tried: t("judgeBadgeTried") }),
    [t],
  );
  useEffect(() => {
    const root = bodyRootRef.current;
    if (root) paintJudgeBadges(root, judge, judgeLabels);
  }, [judge, judgeLabels, contentLang]);

  const setEntry = useCallback((key: string, patch: Partial<JudgeEntry>) => {
    setJudge((prev) => {
      const base: JudgeEntry = prev[key] ?? { accepted: false, attempts: 0, latestVerdict: null, status: IDLE };
      return { ...prev, [key]: { ...base, ...patch } };
    });
  }, []);

  const runtimeCatalogue = useRef<JudgeRuntimeCatalogue | null>(null);

  /**
   * Submit the pane's code for the problem it is open on, then poll the row
   * until the verdict lands. The language is the problem's own row
   * (`lesson.problems[].languageKey`, which the server compares the submit
   * against): `RV-11` found the track's language sent instead, `c` on every
   * track, so a JavaScript or STL problem answered 400 and could never be
   * finished. The version id comes from the runner's live catalogue
   * (`docs/24` §3.1), read once per page: the one the pane's program runs on
   * when the catalogue offers it for that language (`LN-WEB02`), else the one
   * the problem's reference solution was verified on
   * (`GRADED_VERSION_BY_LANGUAGE`), else the catalogue's own pick.
   */
  const submitProblem = useCallback(
    async (sourceCode: string, paneVersion: string | null) => {
      const key = activeProblemKey;
      if (!key || !isAuthenticated) return;
      const languageKey = lesson.problems.find((problem) => problem.key === key)?.languageKey ?? languageId;
      if (!languageKey) return;
      setEntry(key, { status: { kind: "submitting" } });
      try {
        runtimeCatalogue.current ??= await fetchJudgeRuntimes();
        const language = runtimeCatalogue.current.languages.find((row) => row.id === languageKey);
        const version =
          language?.versions.find((row) => paneVersion !== null && row.id === paneVersion) ??
          language?.versions.find((row) => row.id === GRADED_VERSION_BY_LANGUAGE[languageKey]) ??
          language?.versions.find((row) => row.isDefault) ??
          language?.versions[0];
        if (!version) throw Object.assign(new Error("no runtime"), { name: "JUDGE_ENGINE_UNAVAILABLE" });
        const { submissionId, queuePosition } = await submitLearnProblem({
          trackSlug,
          moduleKey,
          lessonKey,
          problemKey: key,
          languageKey,
          languageVersion: version.id,
          sourceCode,
        });
        setEntry(key, {
          attempts: (judgeRef.current[key]?.attempts ?? 0) + 1,
          status: { kind: "judging", queuePosition },
        });
        const deadline = Date.now() + JUDGE_WAIT_MS;
        let row = await fetchLearnProblemSubmission({ trackSlug, moduleKey, lessonKey, problemKey: key, submissionId });
        while (row.result.status !== "completed" && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, JUDGE_POLL_MS));
          row = await fetchLearnProblemSubmission({ trackSlug, moduleKey, lessonKey, problemKey: key, submissionId });
          if (row.result.status !== "completed") {
            setEntry(key, { status: { kind: "judging", queuePosition: row.queuePosition ?? null } });
          }
        }
        if (row.result.status !== "completed" || !row.result.verdict) {
          setEntry(key, { status: { kind: "error", code: "JUDGE_TIMEOUT" } });
          return;
        }
        const verdict = row.result.verdict;
        setEntry(key, {
          accepted: judgeRef.current[key]?.accepted || verdict === "accepted",
          latestVerdict: verdict,
          status: {
            kind: "done",
            verdict,
            hidden: row.result.hidden ?? null,
            compileOutput: row.result.compileOutput ?? null,
            // `EX-FNC09`: the sample tests' verdicts, the checks list's marks.
            samples: [...(row.result.testResults ?? [])].sort((a, b) => a.idx - b.idx).map((test) => test.verdict),
          },
        });
        if (verdict === "accepted") {
          // The settlement tick writes XP and, on the last problem, the
          // completion, a few seconds after the verdict; read both back then.
          const refresh = () => {
            void queryClient.invalidateQueries({ queryKey: ["learn", "lesson", trackSlug, moduleKey, lessonKey, "viewer"] });
            void queryClient.invalidateQueries({ queryKey: ["learn", "track", trackSlug, "viewer"] });
          };
          refresh();
          window.setTimeout(refresh, SETTLEMENT_REFRESH_MS);
        }
      } catch (error) {
        const code = error instanceof Error && error.name ? error.name : "ERROR";
        setEntry(key, { status: { kind: "error", code } });
      }
    },
    [activeProblemKey, isAuthenticated, languageId, lesson.problems, lessonKey, moduleKey, queryClient, setEntry, trackSlug],
  );

  const judgeBar = useMemo<LearnJudgeBar | null>(() => {
    if (!activeProblemKey || !judgedKeys.includes(activeProblemKey)) return null;
    const entry = judge[activeProblemKey] ?? { accepted: false, attempts: 0, latestVerdict: null, status: IDLE };
    const locked = lockedKeys.includes(activeProblemKey);
    const fn = lesson.problems.find((problem) => problem.key === activeProblemKey)?.function;
    const { status } = entry;
    return {
      problemKey: activeProblemKey,
      accepted: entry.accepted,
      attempts: entry.attempts,
      latestVerdict: entry.latestVerdict,
      status: entry.status,
      // `LN-ACC01`: a Pro problem's judge is Pro; the server refuses it too.
      onSubmit:
        isAuthenticated && !locked
          ? (source: string, version: string | null) => void submitProblem(source, version)
          : null,
      signInHref,
      locked: locked ? { trackHref: `/${locale}/learn/${trackSlug}`, proHref: `/${locale}/learn/pro` } : null,
      // `EX-FNC09`: a function problem's checks list, marked by this tab's last check.
      function: fn
        ? {
            fn,
            checks: {
              verdicts: status.kind === "done" ? (status.samples ?? []) : null,
              busy: status.kind === "submitting" || status.kind === "judging",
              hidden: status.kind === "done" ? status.hidden : null,
            },
          }
        : null,
    };
  }, [activeProblemKey, isAuthenticated, judge, judgedKeys, lesson.problems, locale, lockedKeys, signInHref, submitProblem, trackSlug]);

  /**
   * The rail's sections: this lesson's own `h2`s (a project brief's `h3`s, since
   * it has no `h2`), then the checkpoint and the doubts.
   */
  const checkpointLabel = t("checkpointHeading");
  const onEnhanced = useCallback(
    (root: HTMLElement) => {
      bodyRootRef.current = root;
      paintJudgeBadges(root, judgeRef.current, judgeLabels);
      // `LN-ACC01`: where each locked problem's Pro card is mounted. Re-read on
      // every enhance, because React can recreate the stored HTML's subtree.
      const mounts = Array.from(root.querySelectorAll<HTMLElement>(PROBLEM_LOCK_SELECTOR));
      setLockMounts((prev) =>
        prev.length === mounts.length && prev.every((el, i) => el === mounts[i]) ? prev : mounts,
      );
      const found: Array<{ el: HTMLElement; label: string }> = [];
      sectionHeadings(root).forEach((heading, index) => {
        if (!heading.id) heading.id = `learn-h-${index + 1}`;
        found.push({ el: heading, label: sectionLabel(heading) });
      });
      const checkpoint = root.querySelector<HTMLElement>("[data-pc-checkpoint]");
      if (checkpoint) {
        checkpoint.id ||= "learn-checkpoint";
        found.push({ el: checkpoint, label: checkpointLabel });
      }
      const doubts = root.querySelector<HTMLElement>(".pc-doubts");
      const doubtsLabel = doubts?.querySelector(".pc-doubts-title")?.textContent?.trim();
      if (doubts && doubtsLabel) {
        doubts.id ||= "learn-doubts";
        found.push({ el: doubts, label: doubtsLabel });
      }
      found.sort((a, b) =>
        a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
      );
      const next = found.map(({ el, label }) => ({ id: el.id, label }));
      setSections((prev) => (sameSections(prev, next) ? prev : next));
    },
    [checkpointLabel, judgeLabels],
  );

  // The progress hairline and the rail's current section, one frame per scroll.
  // The hairline is written to the DOM rather than to state: a scroll should not
  // re-render the lesson.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const article = articleRef.current;
      if (article && progressRef.current) {
        const rect = article.getBoundingClientRect();
        const total = rect.height - window.innerHeight + TOP_PX;
        const ratio = total > 0 ? Math.min(1, Math.max(0, (TOP_PX - rect.top) / total)) : 1;
        progressRef.current.style.transform = `scaleX(${ratio})`;
      }
      let active: string | null = sections[0]?.id ?? null;
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= TOP_PX + 96) active = section.id;
        else break;
      }
      setActiveSectionId((prev) => (prev === active ? prev : active));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [sections]);

  const runBlocks = useMemo(
    () => ({
      label: t("runCode"),
      canRun: (blockLang: string | undefined, code: string) =>
        canRunBlockInTrack(trackLanguageId, blockLang, code),
    }),
    [trackLanguageId, t],
  );

  /** Opened with no program in hand, the editor starts on the lesson's first one. */
  const starterRequest = useMemo<LearnRunRequest | null>(() => {
    if (!editorReady) return null;
    const first = firstLessonProgram(lesson.content_en);
    return first
      ? {
          nonce: 0,
          sourceCode: first.code,
          autoRun: false,
          languageVersion: first.languageVersion,
          languageId: first.languageId,
          stdin: first.stdin,
        }
      : null;
  }, [editorReady, lesson.content_en]);

  const paneShown = twoPane && paneOpen && editorAllowed;
  const railCollapsed = paneShown && !railExpanded;

  /** `RV-14`: the splitter answers the arrow keys, like the Playground's own. */
  const onSplitterKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // The pane is on the right: the splitter moving left makes it wider.
      const delta = event.key === "ArrowLeft" ? SPLITTER_KEY_STEP : event.key === "ArrowRight" ? -SPLITTER_KEY_STEP : 0;
      if (!delta) return;
      event.preventDefault();
      setPanePercent((value) => {
        const next = Math.min(MAX_PANE_PERCENT, Math.max(MIN_PANE_PERCENT, value + delta));
        persist(PANE_WIDTH_KEY, String(Math.round(next)));
        return next;
      });
    },
    [persist],
  );

  const editorPane = languageId ? (
    <LearnEditorPane
      judge={judgeBar}
      languageId={languageId}
      locale={locale}
      onClose={closeEditor}
      request={runRequest ?? starterRequest}
    />
  ) : null;

  const langSwitch = (
    <div
      aria-label={t("languageLabel")}
      className="inline-flex shrink-0 rounded-[10px] bg-neutral-100 p-[3px] text-xs font-semibold"
      role="group"
    >
      {(["en", "bn"] as const).map((code) => (
        <button
          aria-pressed={contentLang === code}
          className={cn(
            "rounded-[7px] px-2.5 py-[5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            contentLang === code
              ? "bg-white text-neutral-900 shadow-sm"
              : "text-neutral-500 hover:text-neutral-900",
          )}
          key={code}
          lang={code}
          onClick={() => selectLang(code)}
          type="button"
        >
          {code === "en" ? "EN" : "বাংলা"}
        </button>
      ))}
    </div>
  );

  return (
    <div
      className="learn-reader flex min-h-screen flex-col bg-neutral-50"
      onFocusCapture={prefetchLessonLink}
      onPointerOverCapture={prefetchLessonLink}
    >
      <header className="sticky top-0 z-30 bg-white">
        <div className="grid h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-neutral-100 px-4 sm:px-5 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              aria-label="Progsity"
              className="hidden shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:block"
              href={`/${locale}`}
            >
              <Image alt="" className="h-[22px] w-auto" height={34} priority src="/logo.svg" width={138} />
            </Link>
            <span aria-hidden className="hidden h-5 w-px shrink-0 bg-neutral-200 sm:block" />
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5 text-[13px]">
                <Link
                  className="flex min-w-0 items-center gap-1 truncate font-medium text-neutral-500 transition-colors hover:text-primary-ink"
                  href={`/${locale}/learn/${trackSlug}`}
                >
                  <ChevronLeft aria-hidden className="h-4 w-4 shrink-0 sm:hidden" />
                  <span className="truncate">{trackTitle}</span>
                </Link>
                <ChevronRight aria-hidden className="hidden h-3.5 w-3.5 shrink-0 text-neutral-300 lg:block" />
                <span className="hidden truncate font-semibold text-neutral-900 lg:block">{moduleLabel}</span>
              </div>
              <p className="truncate text-xs text-neutral-500 lg:hidden">
                {positionText ? `${positionText} · ` : ""}
                {moduleTitle}
              </p>
            </div>
          </div>

          {positionText ? (
            <div className="hidden items-center gap-3 lg:flex">
              <span className="whitespace-nowrap text-xs font-semibold text-neutral-600">{positionText}</span>
              <span aria-hidden className="flex gap-1">
                {moduleLessons.map((row) => (
                  <span
                    className={cn(
                      "h-[5px] w-3.5 rounded-sm",
                      row.id === lesson.id
                        ? "bg-primary"
                        : completedLessonIds.includes(row.id)
                          ? "bg-primary/40"
                          : "bg-neutral-200",
                    )}
                    key={row.id}
                  />
                ))}
              </span>
            </div>
          ) : (
            <span className="hidden lg:block" />
          )}

          <div className="flex items-center justify-end gap-2 sm:gap-2.5">
            {langSwitch}
            <Button
              className="h-9 gap-1.5 border border-neutral-200 px-2.5 text-[13px] font-semibold text-neutral-800 sm:px-3"
              onClick={() => setSyllabusOpen(true)}
              variant="tertiary"
            >
              <ListTree aria-hidden className="h-4 w-4" />
              <span className="sr-only sm:not-sr-only">{t("openSyllabus")}</span>
            </Button>
            {editorAllowed ? (
              <Button
                aria-pressed={paneShown}
                className={cn(
                  "hidden h-9 gap-1.5 px-3 text-[13px] font-semibold lg:inline-flex",
                  paneShown
                    ? "bg-neutral-900 text-white hover:bg-neutral-800 hover:text-white"
                    : "border border-neutral-200 text-neutral-800",
                )}
                onClick={() => (paneShown ? closeEditor() : openEditor())}
                variant="tertiary"
              >
                <PanelRight aria-hidden className="h-4 w-4" />
                {t("editor")}
              </Button>
            ) : null}
          </div>
        </div>
        <div aria-hidden className="h-[3px] bg-neutral-100">
          <div
            className="h-full origin-left bg-primary"
            ref={progressRef}
            style={{ transform: "scaleX(0)" }}
          />
        </div>
      </header>

      <div className="flex w-full flex-1 items-stretch">
        {currentModule ? (
          <aside className="hidden shrink-0 lg:block">
            <div className="sticky" style={{ top: TOP_PX, height: `calc(100vh - ${TOP_PX}px)` }}>
              <LearnReaderRail
                activeSectionId={activeSectionId}
                canCollapse={paneShown}
                collapsed={railCollapsed}
                completedLessonIds={completedLessonIds}
                contentLang={contentLang}
                currentLessonId={lesson.id}
                locale={locale}
                module={currentModule}
                moduleCount={allModules.length}
                moduleNumber={moduleIndex}
                nextModules={nextModules}
                onOpenSyllabus={() => setSyllabusOpen(true)}
                onToggleCollapsed={() => setRailExpanded((open) => !open)}
                sections={sections}
              />
            </div>
          </aside>
        ) : null}

        <div className="flex min-w-0 flex-1 items-stretch" ref={frameRef}>
          <article
            className={cn(
              "min-w-0 flex-1 px-4 pb-28 pt-8 sm:px-6 lg:pt-11 lg:pb-16",
              paneShown ? "lg:px-10" : "lg:px-12",
            )}
            ref={articleRef}
          >
            <Container className="px-0 lg:px-0" width="read">
              <p
                className={cn(
                  "font-[family-name:var(--font-heading)] text-xs font-bold tracking-[0.14em] text-primary-ink",
                  // Capitals are a Latin idea: in Bangla they only shout the English words.
                  contentLang === "en" && "uppercase",
                )}
              >
                {moduleLabel}
              </p>
              <h1 className="mt-2.5 font-[family-name:var(--font-heading)] text-[28px] font-bold leading-[1.15] tracking-[-0.015em] text-neutral-950 lg:text-4xl">
                {title}
              </h1>
              <div className="mt-3.5 flex flex-wrap gap-2">
                <StatusBadge label={tl(ACCESS_LABEL[lesson.learnAccess])} tone={ACCESS_TONE[lesson.learnAccess]} />
                <StatusBadge icon={KindIcon} label={tl(KIND_LABEL[kind])} tone="neutral" />
                {positionText ? (
                  <StatusBadge className="hidden sm:inline-flex" label={positionText} tone="neutral" />
                ) : null}
                {lesson.minutes !== null ? (
                  <StatusBadge label={t("minutes", { count: num(lesson.minutes) })} tone="neutral" />
                ) : null}
              </div>

              <div className="mt-7">
                {lesson.type === "quiz" && lesson.access === "granted" && lesson.test ? (
                  <LearnModuleTest
                    covers={moduleLessons
                      .filter((row) => lessonKind(row) === "read")
                      .map((row) => pick(row))}
                    lessonKey={lessonKey}
                    locale={locale}
                    moduleKey={moduleKey}
                    nextHref={nav.next ? learnLessonHref(locale, trackSlug, nav.next) : null}
                    signInHref={signInHref}
                    summary={lesson.test}
                    trackSlug={trackSlug}
                  />
                ) : (
                  <LearnLessonBody
                    checkpoint={
                      lesson.checkpoints.length > 0 ? (
                        <LearnCheckpoint
                          checkpoints={lesson.checkpoints}
                          completed={(viewer?.openedSteps ?? []).includes("checkpoint")}
                          contentLang={contentLang}
                          onComplete={() => recordStep("checkpoint", "checkpoint")}
                          seed={lesson.id}
                        />
                      ) : undefined
                    }
                    contentLang={contentLang}
                    hintLadder={{
                      labels: {
                        // `t.raw` because the ladder's buttons are built in the
                        // DOM: the label is a template it fills per rung.
                        hint: t.raw("hint") as string,
                        solution: t("showSolution"),
                        referenceSolution: t("referenceSolution"),
                      },
                      solutions,
                      openedSteps: viewer?.openedSteps ?? [],
                      onOpen: (step) => recordStep("hint", step),
                      lockedKeys,
                    }}
                    lesson={lesson}
                    locale={locale}
                    onEnhanced={onEnhanced}
                    onRunCode={(code, autoRun, meta) => openEditor(code, autoRun, meta)}
                    run={editorAllowed ? runBlocks : undefined}
                    accessPending={accessPending}
                    signInRedirect={pathname}
                    title={title}
                    trackSlug={trackSlug}
                  />
                )}
                {lesson.access === "granted" && !problemLocksPending
                  ? lockMounts
                      .filter((mount) => mount.isConnected)
                      .map((mount) =>
                        createPortal(
                          <LearnProblemLock
                            locale={locale}
                            signInHref={isAuthenticated ? null : signInHref}
                            trackSlug={trackSlug}
                          />,
                          mount,
                          mount.dataset.pcProblemLock ?? undefined,
                        ),
                      )
                  : null}
              </div>

              {/* The module test, offered by the module's last text lesson only
                  (docs/35 §5 item 3). */}
              {lesson.moduleTest ? (
                <div className="mt-12 rounded-[var(--radius)] border border-neutral-200 bg-white p-5">
                  <p className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                    <Trophy aria-hidden className="h-4 w-4 text-primary" />
                    {t("moduleTestHeading")}
                  </p>
                  <p className="mt-1.5 text-sm text-neutral-600">{t("moduleTestBody")}</p>
                  <Button asChild className="mt-4" variant="secondary">
                    <Link href={learnLessonHref(locale, trackSlug, lesson.moduleTest)}>
                      {t("moduleTestCta")}
                    </Link>
                  </Button>
                </div>
              ) : null}

              {showFinish ? (
                <section
                  aria-live="polite"
                  className="mt-12 flex flex-col gap-5 rounded-[var(--radius)] bg-neutral-900 p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-7"
                >
                  <div className="min-w-0">
                    {positionInModule > 0 ? (
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-accent-peach">
                        {t("finishEyebrow", { n: num(positionInModule) })}
                      </p>
                    ) : null}
                    <p className="mt-2 font-[family-name:var(--font-heading)] text-lg font-bold leading-snug sm:text-xl">
                      {isDone ? t("finishDoneTitle") : finishGated ? t("judgeFinishTitle") : t("finishTitle")}
                    </p>
                    {reachableKeys.length > 0 ? (
                      <p className="mt-1.5 text-sm font-semibold text-accent-peach" data-testid="judge-progress">
                        {lockedJudgedCount > 0
                          ? t("judgeProgressFree", { done: num(acceptedCount), total: num(reachableKeys.length) })
                          : t("judgeProgress", { done: num(acceptedCount), total: num(reachableKeys.length) })}
                      </p>
                    ) : null}
                    {/* `LN-ACC01`: what the Pro problems of this set would add. */}
                    {lockedJudgedCount > 0 && !problemLocksPending ? (
                      <p className="mt-1.5 text-sm text-neutral-300" data-testid="judge-pro-locked">
                        {t("judgeProLocked", { count: lockedJudgedCount })}{" "}
                        <Link
                          className="font-semibold text-accent-peach underline underline-offset-4 hover:text-white"
                          href={`/${locale}/learn/pro`}
                        >
                          {t("lockGetPro")}
                        </Link>
                      </p>
                    ) : null}
                    <p className="mt-1.5 text-sm text-neutral-300">
                      {nav.next ? t("finishNext", { title: pick(nav.next) }) : t("finishLast")}
                    </p>
                  </div>
                  {isAuthenticated && !isDone && !finishGated ? (
                    <Button
                      className="h-12 shrink-0 px-5 text-[15px] font-bold"
                      disabled={marking}
                      onClick={() => void markDone()}
                      type="button"
                    >
                      <Check aria-hidden className="h-4 w-4" strokeWidth={2.8} />
                      {nav.next ? t("doneNext") : t("doneOnly")}
                      {nav.next ? <ArrowRight aria-hidden className="h-4 w-4" /> : null}
                    </Button>
                  ) : (
                    <div className="flex shrink-0 flex-col items-start gap-2.5 sm:items-end">
                      {isDone ? (
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-300">
                          <Check aria-hidden className="h-4 w-4" strokeWidth={2.8} />
                          {t("doneAlready")}
                        </span>
                      ) : null}
                      {nav.next ? (
                        <Button asChild className="h-12 px-5 text-[15px] font-bold">
                          <Link href={learnLessonHref(locale, trackSlug, nav.next)}>
                            {t("nextLesson")}
                            <ArrowRight aria-hidden className="h-4 w-4" />
                          </Link>
                        </Button>
                      ) : null}
                      {!isAuthenticated && !authLoading ? (
                        <Link
                          className="text-sm text-neutral-300 underline underline-offset-4 hover:text-white"
                          href={signInHref}
                        >
                          {t("signInToSave")}
                        </Link>
                      ) : null}
                    </div>
                  )}
                </section>
              ) : null}
              {markError ? (
                <p className="mt-3 text-sm font-medium text-red-700" role="alert">
                  {t("doneError")}
                </p>
              ) : null}

              <nav
                aria-label={t("lessonNav")}
                className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm"
              >
                {nav.prev ? (
                  <Link
                    className="inline-flex min-w-0 items-center gap-1.5 text-neutral-500 transition-colors hover:text-primary-ink"
                    href={learnLessonHref(locale, trackSlug, nav.prev)}
                  >
                    <ChevronLeft aria-hidden className="h-4 w-4 shrink-0" />
                    <span className="max-w-[22rem] truncate">
                      {t("previousLesson", { title: pick(nav.prev) })}
                    </span>
                  </Link>
                ) : (
                  <span />
                )}
                {nav.next && !showFinish ? (
                  <Link
                    className="inline-flex min-w-0 items-center gap-1.5 font-semibold text-primary-ink hover:underline"
                    href={learnLessonHref(locale, trackSlug, nav.next)}
                  >
                    <span className="max-w-[22rem] truncate">{t("finishNext", { title: pick(nav.next) })}</span>
                    <ChevronRight aria-hidden className="h-4 w-4 shrink-0" />
                  </Link>
                ) : null}
                <p className="hidden items-center gap-1.5 text-xs text-neutral-400 lg:flex">
                  <span className="sr-only">{t("keyboardHint")}</span>
                  <span aria-hidden className="flex items-center gap-1.5">
                    <kbd className="rounded border border-neutral-200 bg-white px-1.5 font-mono text-[11px] text-neutral-600">J</kbd>
                    {t("keysNext")} ·
                    <kbd className="rounded border border-neutral-200 bg-white px-1.5 font-mono text-[11px] text-neutral-600">K</kbd>
                    {t("keysPrev")}
                    {editorAllowed ? (
                      <>
                        {" ·"}
                        <kbd className="rounded border border-neutral-200 bg-white px-1.5 font-mono text-[11px] text-neutral-600">R</kbd>
                        {t("keysRun")}
                      </>
                    ) : null}
                  </span>
                </p>
              </nav>
            </Container>
          </article>

          {paneShown ? (
            <>
              <div
                aria-label={t("resizePane")}
                aria-orientation="vertical"
                aria-valuemax={MAX_PANE_PERCENT}
                aria-valuemin={MIN_PANE_PERCENT}
                aria-valuenow={Math.round(panePercent)}
                className="group relative w-2 shrink-0 cursor-col-resize bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                onKeyDown={onSplitterKeyDown}
                onPointerDown={onSplitterDown}
                role="separator"
                tabIndex={0}
              >
                <span
                  className="sticky block"
                  style={{ top: TOP_PX, height: `calc(100vh - ${TOP_PX}px)` }}
                >
                  <span className="absolute left-1/2 top-1/2 h-10 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-sm bg-neutral-300 transition-colors group-hover:bg-primary" />
                </span>
              </div>
              <aside
                className="shrink-0"
                style={{ width: `max(${panePercent}%, ${MIN_PANE_PX}px)` }}
              >
                <div
                  className="sticky overflow-hidden"
                  style={{ top: TOP_PX, height: `calc(100vh - ${TOP_PX}px)` }}
                >
                  {editorReady ? editorPane : <div className="h-full bg-neutral-950" />}
                </div>
              </aside>
            </>
          ) : null}
        </div>
      </div>

      {/* Phone and small tablet: previous, the editor, next. */}
      {!twoPane ? (
        <div className="sticky bottom-0 z-30 flex items-center gap-2 border-t border-neutral-200 bg-white px-4 py-2.5">
          {nav.prev ? (
            <Button
              asChild
              className="h-11 w-11 shrink-0 border border-neutral-200 px-0"
              variant="tertiary"
            >
              <Link
                aria-label={t("previousLesson", { title: pick(nav.prev) })}
                href={learnLessonHref(locale, trackSlug, nav.prev)}
              >
                <ChevronLeft aria-hidden className="h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <span className="w-11 shrink-0" />
          )}
          {editorAllowed ? (
            <Button className="h-11 flex-1 gap-2 font-bold" onClick={() => openEditor()} type="button">
              <Play aria-hidden className="h-4 w-4 fill-current" />
              {t("openPlayground")}
            </Button>
          ) : (
            <span className="flex-1" />
          )}
          {nav.next ? (
            <Button
              asChild
              className="h-11 w-11 shrink-0 border border-neutral-200 px-0"
              variant="tertiary"
            >
              <Link
                aria-label={t("finishNext", { title: pick(nav.next) })}
                href={learnLessonHref(locale, trackSlug, nav.next)}
              >
                <ChevronRight aria-hidden className="h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <span className="w-11 shrink-0" />
          )}
        </div>
      ) : null}

      {!twoPane && sheetOpen && editorAllowed ? (
        <div className="fixed inset-0 z-40 flex flex-col bg-neutral-950">{editorPane}</div>
      ) : null}

      <LearnSyllabusPanel
        completedLessonIds={completedLessonIds}
        currentLessonId={lesson.id}
        locale={locale}
        onClose={() => setSyllabusOpen(false)}
        open={syllabusOpen}
        parts={parts}
      />
    </div>
  );
}
